import React, { useState, useContext, useEffect } from "react";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { SettingsContext } from "../contexts/SettingsContext";
import { useSnackbar } from "notistack";
import { ServiceRenderedRecord, ServiceRecord } from "../repositories/types";
import { useRepositories } from "../repositories";
import { roundToPence } from "../utils/money";

interface ActivityTableProps {
  data: ServiceRenderedRecord[] | null | undefined;
  clientId: string;
  updateSource?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableRoot: {
      margin: "16px",
      width: "90%",
    },
    rowActions: {
      display: "flex",
    },
  })
);

interface DisplayActivityRow {
  clientID: string;
  serviceID: string;
  date: string;
  quantity: number;
  mileage: number;
  id: string;
  serviceCost: number;
  mileageTotal?: number | null;
  total: number;
  serviceName: string;
  invoiceId?: string | null;
}

// Rounds a raw mileage cost to the whole-penny value that should be held in
// row/component state (not just applied at render time via formatCurrency).
export const computeMileageTotal = (mileageCost: number): number => roundToPence(mileageCost);

// Computes an activity row's total cost using the same rounded mileage cost as
// computeMileageTotal, so `total` and `mileageTotal` are always derived from
// the same whole-penny mileage figure rather than each rounding independently
// (or not rounding at all).
export const computeActivityTotal = (serviceCost: number, quantity: number, mileageCost: number): number =>
  serviceCost * quantity + roundToPence(mileageCost);

const getDOW = (date: string): string => {
  const d = new Date(date);
  const days = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
  const dayNumber = d.getDay();
  return (
    days[dayNumber] +
    " " +
    d.getDate() +
    "-" +
    (d.getMonth() + 1 < 10 ? "0" + (d.getMonth() + 1) : d.getMonth() + 1) +
    "-" +
    d.getFullYear()
  );
};

const ActivityTable: React.FC<ActivityTableProps> = (props: ActivityTableProps) => {
  const classes = useStyles();
  const [rows, setRows] = useState<DisplayActivityRow[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [services, setServices] = useState<ServiceRecord[]>([]);
  const { activities } = useRepositories();

  useEffect(() => {
    activities
      .listServices()
      .then((s) => setRows((prev) => { setServices(s.sort((a, b) => (a.name < b.name ? -1 : 1))); return prev; }))
      .catch(() => enqueueSnackbar("Failed to load services", { variant: "error" }));
  }, [enqueueSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newRows: DisplayActivityRow[] = (props.data ?? [])
      .filter((x): x is ServiceRenderedRecord => x !== null)
      .map((x) => ({
        clientID: props.clientId,
        serviceID: x.serviceId,
        date: x.date,
        serviceName: x.serviceName,
        quantity: x.quantity,
        mileage: x.mileage ?? 0,
        id: x.id,
        serviceCost: x.serviceCost ?? 0,
        mileageTotal: computeMileageTotal(x.mileageCost ?? 0),
        invoiceId: x.invoiceID,
        total: computeActivityTotal(x.serviceCost ?? 0, x.quantity ?? 1, x.mileageCost ?? 0),
      }));
    newRows.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
    setRows(newRows);
  }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteRow = (id: string) => {
    activities
      .deleteRendered(id)
      .then(() => {
        setRows(rows.filter((i) => i.id !== id));
        props.updateSource?.();
      })
      .catch(() => {
        enqueueSnackbar("Failed to delete activity", { variant: "error" });
      });
  };

  return (
    <TableContainer component={Paper} className={classes.tableRoot}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Service</TableCell>
            <TableCell>Quantity</TableCell>
            <TableCell>Mileage</TableCell>
            <TableCell>Total Mileage</TableCell>
            <TableCell>Cost Per Activity</TableCell>
            <TableCell>Total Cost</TableCell>
            <TableCell>Invoiced?</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row: DisplayActivityRow) => (
            <EditableTableRow
              key={row.id}
              {...row}
              delete={deleteRow}
              updateSource={props.updateSource}
              services={services}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const EditableTableRow: React.FC<any> = (props) => {
  const [isEditable, setEditable] = useState(false);
  const [data, setData] = useState(Object.assign({}, props));
  const [original, setOriginal] = useState(Object.assign({}, props));
  const classes = useStyles();
  const { activities } = useRepositories();

  const { settings } = useContext(SettingsContext);

  const save = async () => {
    const mileageCost = data.mileage * (settings?.mileageCost ?? 0);
    await activities.updateRendered(
      data.id,
      data.date,
      data.clientID,
      data.serviceID,
      data.mileage,
      data.quantity,
      mileageCost,
      data.serviceCost
    );
    const roundedMileageTotal = computeMileageTotal(mileageCost);
    const total = computeActivityTotal(data.serviceCost, data.quantity, mileageCost);
    setData({
      ...data,
      mileageTotal: roundedMileageTotal,
      total,
    });
    setOriginal({ ...data, mileageTotal: roundedMileageTotal, total });
    setEditable(false);
    if (props.updateSource) {
      props.updateSource();
    }
  };

  const updateSelectedService = (e: SelectChangeEvent) => {
    const newService = props.services.find((s: ServiceRecord) => s.id === e.target.value);
    setData({
      ...data,
      serviceID: e.target.value as string,
      serviceName: newService.name,
      serviceCost: newService.cost,
    });
  };

  useEffect(() => {
    const mileageCost = (settings?.mileageCost ?? 0) * data.mileage;
    setData((d: any) => ({
      ...d,
      mileageTotal: computeMileageTotal(mileageCost),
      total: computeActivityTotal(data.serviceCost, data.quantity ?? 0, mileageCost),
    }));
  }, [data.mileage, data.serviceCost, data.quantity, settings]);

  return (
    <TableRow>
      {!isEditable ? (
        <>
          <TableCell component="th" scope="row">
            {getDOW(data.date)}
          </TableCell>
          <TableCell>{data.serviceName}</TableCell>
          <TableCell>{data.quantity}</TableCell>
          <TableCell>{data.mileage}</TableCell>
          <TableCell>£{formatCurrency(data.mileageTotal ?? 0)}</TableCell>
          <TableCell>£{formatCurrency(data.serviceCost)}</TableCell>
          <TableCell>£{formatCurrency(data.total)}</TableCell>
          <TableCell>
            {data.invoiceId ? <DoneIcon aria-label="invoiced-yes" /> : <CloseIcon aria-label="invoiced-no" />}
          </TableCell>
          <TableCell className={classes.rowActions}>
            <IconButton disabled={data.invoiceId} onClick={() => setEditable(true)} size="large">
              <EditIcon />
            </IconButton>
            <IconButton disabled={data.invoiceId} onClick={() => props.delete(data.id)} size="large">
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell component="th" scope="row">
            {getDOW(data.date)}
          </TableCell>
          <TableCell>
            <Select value={data.serviceID} onChange={updateSelectedService}>
              {props.services.map((s: ServiceRecord, key: number) => (
                <MenuItem key={key} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </TableCell>
          <TableCell>
            <TextField
              value={data.quantity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({ ...data, quantity: e.target.value })}
            />
          </TableCell>
          <TableCell>
            <TextField
              value={data.mileage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({ ...data, mileage: e.target.value })}
            />
          </TableCell>
          <TableCell>£{formatCurrency(data.mileageTotal ?? 0)}</TableCell>
          <TableCell>£{formatCurrency(data.serviceCost)}</TableCell>
          <TableCell>£{formatCurrency(data.total)}</TableCell>
          <TableCell></TableCell>
          <TableCell>
            <IconButton onClick={save} size="large">
              <SaveIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setEditable(false);
                setData(original);
              }}
              size="large"
            >
              <CancelIcon />
            </IconButton>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

const formatCurrency = (cost: number) => {
  return (roundToPence(cost) / 100).toFixed(2);
};

export default ActivityTable;
